import { DatabaseUnavailableError, PersistenceError } from '../errors/PersistenceError';
import type { Clock } from '../ports/Clock';
import type { LocalDatabase } from '../ports/LocalDatabase';
import type { ProfileRepository } from '../ports/ProfileRepository';
import type { Profile } from '../../domain/profile/Profile';
import { asDomainId, type ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';

export const DEFAULT_PROFILE_ID = asDomainId<ProfileId>('local-default-profile');

export interface DefaultProfileConfiguration {
  readonly displayName: string;
  readonly locale: string;
  readonly timeZone: string;
}

export interface PersistenceInitializationResult {
  readonly profileId: ProfileId;
  readonly profileCreated: boolean;
  readonly schemaVersion: number;
}

export class InitializeLocalPersistence {
  public constructor(
    private readonly database: LocalDatabase,
    private readonly profiles: ProfileRepository,
    private readonly clock: Clock,
  ) {}

  public async execute(
    configuration: DefaultProfileConfiguration,
  ): Promise<Result<PersistenceInitializationResult, PersistenceError>> {
    try {
      await this.database.open();
      const health = await this.database.healthCheck();
      const existingProfile = await this.profiles.findById(DEFAULT_PROFILE_ID);

      if (existingProfile) {
        return success({
          profileId: existingProfile.id,
          profileCreated: false,
          schemaVersion: health.schemaVersion,
        });
      }

      const timestamp = this.clock.now().toISOString();
      const profile: Profile = {
        id: DEFAULT_PROFILE_ID,
        displayName: configuration.displayName,
        locale: configuration.locale,
        timeZone: configuration.timeZone,
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      await this.profiles.save(profile);

      return success({
        profileId: profile.id,
        profileCreated: true,
        schemaVersion: health.schemaVersion,
      });
    } catch (error) {
      return failure(error instanceof PersistenceError ? error : new DatabaseUnavailableError());
    }
  }
}
